import React from "react";
import { ArrowRightOutlined, LockOutlined } from "@ant-design/icons";
import { LocalStorageService } from "../../../../base/services/local-storage.service";
import { useNavigate } from "react-router-dom";
import AuthService from "../../../../base/services/authentication.service";
import { UserRolesEnum } from "../../../auth/userRoles.enum";
import { httpService } from "../../../../base/services/httpService.service";
import { Tooltip, notification } from "antd";
import { toAbsoluteUrl } from "../../../../../_metronic/_helpers";
import { EntityTypeEnum } from "../../../../utils/enums";
import {
   readOrganizationLock,
   rememberLockedOrganization,
   rememberSelectedStatus
} from "../../../../components/Billing/billingSignIn";
import { announceOrganizationLock } from "../../../../components/Billing/paymentRequired";
import { useBillingStore } from "../../../../components/Billing/billingStore";

const Organization = ({
   organizationId,
   organizationName,
   organizationMemberId,
   isDeactivatedUser,
   isDeactivatedOrganization,
   lock
}) => {
   const [api, contextHolder] = notification.useNotification();
   const navigate = useNavigate();

   const openNotificationWithIcon = (type, message, description) => {
      if (type === "error") {
         api[type]({
            message,
            description
         });
      }
   };

   const navigateToRoute = () => {
      if (
         AuthService.isAuthorized([
            UserRolesEnum.SuperAdmin,
            UserRolesEnum.Organization,
            UserRolesEnum.AdminOfOrganization,
            UserRolesEnum.SeniorProjectManager
         ])
      ) {
         navigate("/admin-workspaces");
      } else if (AuthService.isAuthorized([UserRolesEnum.DataCollector])) {
         navigate("/mytasks");
      } else {
         navigate("/workspace");
      }
   };

   const seeWorkspace = async () => {
      if (isDeactivatedOrganization) return;
      const userId = LocalStorageService.getItem("user")?.id;
      try {
         const organizationParams = new URLSearchParams({
            userId: userId,
            organizationId: organizationId
         });
         await httpService.get(
            `/Account/selectOrganization?${organizationParams.toString()}`,
            (response) => {
               if (!response) return;
               // Held at billing_only and not this user's to pay for: it isn't
               // opened, and its card shows it locked (billingSignIn.js).
               const lock = readOrganizationLock(response.data, organizationId);
               if (lock) {
                  rememberLockedOrganization(response.data, organizationId);
                  announceOrganizationLock(lock.reason, organizationName);
                  return;
               }
               // Picked by hand, as "Try again" on the billing page is: what
               // selectOrganization says now is taken over an earlier 402's
               // hold, and a 402 that still applies holds it again.
               useBillingStore.getState().releaseGateHold(organizationId);
               // What the new billing flow says about this organization — the
               // routes hold one at billing_only on its billing page.
               rememberSelectedStatus(response.data, organizationId);
               LocalStorageService.setItem("atoken", response.data.token);
               // Each organization's refresh token renews that organization's
               // session, and reports its billing (base/utils/http.js).
               if (response.data.refreshToken) {
                  LocalStorageService.setItem("rtoken", response.data.refreshToken);
               }
               LocalStorageService.setItem("tokenExpire", response.data.tokenExpire);
               LocalStorageService.setItem("organizationId", organizationId);
               LocalStorageService.setItem("organizationName", organizationName);
               // The workspace and project picked in the previous organization are not
               // this one's; every request for them would now answer 404.
               LocalStorageService.removeItem("currentWorkspaceId");
               LocalStorageService.removeItem("currentProjectID");
               LocalStorageService.setItem("roles", response.data.userRole);

               if (isDeactivatedUser) {
                  const params = new URLSearchParams({
                     entityType: EntityTypeEnum.OrganizationMember,
                     isDeleted: false
                  });
                  httpService
                     .put(
                        `/DataCollection/${organizationMemberId}/toggleDeleteOrRestore?${params.toString()}`,
                        {},
                        (response) => {
                           if (!response) return;
                           LocalStorageService.setItem("reactivatedAccount", true);
                           navigateToRoute();
                        }
                     )
                     .catch((error) => {
                        openNotificationWithIcon(
                           "error",
                           "Unable to restore the user. Please try again later."
                        );
                     });
               } else {
                  navigateToRoute();
               }
            }
         );
      } catch (error) {
         openNotificationWithIcon("error", "Something went wrong. Please try again.");
      }
   };

   // Locked by its billing (Organizations.jsx): shown the way a deactivated
   // organization is, with a way to look again once its owner has renewed it.
   const isLocked = Boolean(lock) && !isDeactivatedOrganization;

   const content = (
      <div
         className={`organization ${
            isLocked
               ? "deactivatedOrganization"
               : isDeactivatedUser
               ? "deactivatedUser"
               : isDeactivatedOrganization
               ? "deactivatedOrganization"
               : ""
         }`}
      >
         <img
            alt="workspace-svg"
            src={toAbsoluteUrl("/media/bg/workspace-bg.png")}
            className="workspaceBackground"
         />
         <h4>{organizationName}</h4>
         {isLocked ? (
            <div className="seeWorkspaces" onClick={seeWorkspace}>
               <LockOutlined /> Check again
            </div>
         ) : isDeactivatedOrganization ? null : (
            <div className="seeWorkspaces" onClick={seeWorkspace}>
               {isDeactivatedUser ? "Activate account" : "See workspaces"} <ArrowRightOutlined />
            </div>
         )}
      </div>
   );

   return (
      <>
         {contextHolder}
         {isLocked ? (
            <Tooltip title={`${lock.title}. ${lock.description}`}>{content}</Tooltip>
         ) : isDeactivatedUser ? (
            <Tooltip title='Your account in this organization has been deactivated. Click "activate account" if you wish to access it again.'>
               {content}
            </Tooltip>
         ) : isDeactivatedOrganization ? (
            <Tooltip title="This organization has been deactivated.">{content}</Tooltip>
         ) : (
            content
         )}
      </>
   );
};

export default Organization;
